import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { VenueService } from '../../../../services/VenueService';
import {
  VenueContinentGroup,
  VenueEntry,
} from '../../../../models/venue-admin.model';

/**
 * Collapsible venue picker. Replaces the native <select> with a hierarchical
 * continent -> city -> venue tree, plus a pinned "Scanning now" group at the
 * top listing venues that have scanned within the active-scan window.
 *
 * The component is presentational: it receives the venue list and the set of
 * active venue keys from its parent and emits the selected venue key. It does
 * not perform its own data fetching, keeping data ownership with the parent.
 */
@Component({
  selector: 'app-venue-selector',
  standalone: false,
  templateUrl: './venue-selector.component.html',
  styleUrls: ['./venue-selector.component.scss'],
})
export class VenueSelectorComponent implements OnChanges {
  /** Flat, name-sorted venue list (as returned by VenueService.getVenueList). */
  @Input() venues: VenueEntry[] = [];

  /** Keys of venues currently "scanning now". Empty set hides that group. */
  @Input() activeVenueKeys: Set<string> = new Set<string>();

  /** Currently selected venue key (two-way bindable via selectedKeyChange). */
  @Input() selectedKey: string = '';
  @Output() selectedKeyChange = new EventEmitter<string>();

  /** Emitted whenever the user picks a venue. */
  @Output() venueSelected = new EventEmitter<string>();

  isOpen = false;
  searchTerm = '';

  /** Continent -> city -> venue tree, rebuilt when the venue list changes. */
  tree: VenueContinentGroup[] = [];

  /** Expansion state keyed by group id so open/closed survives re-renders. */
  private readonly expanded = new Set<string>();

  /** Continents that contain at least one active venue. */
  private activeContinents = new Set<string>();
  /** Cities (keyed "continent:city") that contain at least one active venue. */
  private activeCities = new Set<string>();

  constructor(
    private readonly venueService: VenueService,
    private readonly elementRef: ElementRef<HTMLElement>
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['venues']) {
      this.tree = this.venueService.buildVenueTree(this.venues ?? []);
    }
    if (changes['venues'] || changes['activeVenueKeys']) {
      this.recomputeActiveGroups();
    }
  }

  /**
   * Rolls active venues up the tree so a continent or city shows the live dot
   * when any venue beneath it is scanning. Recomputed only when the tree or the
   * active set changes, not on every render.
   */
  private recomputeActiveGroups(): void {
    const continents = new Set<string>();
    const cities = new Set<string>();
    for (const continent of this.tree) {
      for (const city of continent.cities) {
        const hasActive = city.venues.some((venue) => this.activeVenueKeys.has(venue.key));
        if (hasActive) {
          continents.add(continent.continent);
          cities.add(this.cityId(continent.continent, city.city));
        }
      }
    }
    this.activeContinents = continents;
    this.activeCities = cities;
  }

  /** Composite id used to key a city within its continent. */
  cityId(continent: string, city: string): string {
    return continent + ':' + city;
  }

  isVenueActive(key: string): boolean {
    return this.activeVenueKeys.has(key);
  }

  isContinentActive(continent: string): boolean {
    return this.activeContinents.has(continent);
  }

  isCityActive(continent: string, city: string): boolean {
    return this.activeCities.has(this.cityId(continent, city));
  }

  get selectedVenueName(): string {
    const match = this.venues.find((venue) => venue.key === this.selectedKey);
    return match ? match.value.name : 'Please Select a Venue';
  }

  /**
   * When the user only has access to a single venue there is nothing to pick
   * between, so the dropdown is shown as a static label rather than an
   * interactive trigger.
   */
  get isSingleVenue(): boolean {
    return this.venues.length === 1;
  }

  /** Venues that are scanning now, name-sorted, for the pinned group. */
  get activeVenues(): VenueEntry[] {
    if (this.activeVenueKeys.size === 0) {
      return [];
    }
    return this.venues
      .filter((venue) => this.activeVenueKeys.has(venue.key))
      .sort((a, b) => a.value.name.localeCompare(b.value.name));
  }

  toggleOpen(): void {
    this.isOpen = !this.isOpen;
    if (!this.isOpen) {
      this.searchTerm = '';
    }
  }

  isExpanded(id: string): boolean {
    return this.expanded.has(id);
  }

  toggleGroup(id: string): void {
    if (this.expanded.has(id)) {
      this.expanded.delete(id);
    } else {
      this.expanded.add(id);
    }
  }

  selectVenue(key: string): void {
    this.selectedKey = key;
    this.selectedKeyChange.emit(key);
    this.venueSelected.emit(key);
    this.isOpen = false;
    this.searchTerm = '';
  }

  /**
   * Filters a city's venues by the search term. Returns the full list when no
   * term is entered. Matching is case-insensitive against the venue name.
   */
  filterVenues(venues: VenueEntry[]): VenueEntry[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return venues;
    }
    return venues.filter((venue) => venue.value.name.toLowerCase().includes(term));
  }

  /**
   * Whether a continent group has any venue matching the current search term,
   * so empty groups can be hidden while filtering.
   */
  continentHasMatches(group: VenueContinentGroup): boolean {
    return group.cities.some((city) => this.filterVenues(city.venues).length > 0);
  }

  cityHasMatches(venues: VenueEntry[]): boolean {
    return this.filterVenues(venues).length > 0;
  }

  /** trackBy helpers to avoid re-rendering the whole tree on each change. */
  trackByContinent(_index: number, group: VenueContinentGroup): string {
    return group.continent;
  }

  trackByCity(_index: number, city: { city: string }): string {
    return city.city;
  }

  trackByVenue(_index: number, venue: VenueEntry): string {
    return venue.key;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isOpen && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.isOpen = false;
      this.searchTerm = '';
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) {
      this.isOpen = false;
      this.searchTerm = '';
    }
  }
}
